import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import {
    TableRow,
    TableCell,
    TableHeader,
    TableColumn,
    MobileCardRow,
    MobileCardWrapper,
    TableBody,
} from './dashboard/table-components';
import { ArrowLeftIcon, ArrowRightIcon } from '../assets/dashboard-icon.svg';
import './../scss/components/invoice-table-detailed.style.scss';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../hooks/useRedux';
import { fetchTimesheetDeductions, deleteTimesheetWorkingHours } from '../features/timesheet/timesheet.slice';
import { ARTICLE_NUMBER_TO_DEDUCTION_TYPE } from '../constants/deduction-article-mapping.constants';
import { getTimesheetDeductionsApi } from '../api/salary/deduction.service';

interface NotInvoicedDeduction {
    id: string;
    deduction: string;
    customerName: string;
    period: string;
    amount: string;
}

interface NotInvoicedSectionProps {
    onEditDeduction?: (deduction: NotInvoicedDeduction) => void;
    fromDate?: string;
    toDate?: string;
    customerId?: string | number;
    selectedYear?: string;
    selectedMonth?: string;
    searchQuery?: string;
}

const TAB_NOT_INVOICED = 2;

const NotInvoicedSection: React.FC<NotInvoicedSectionProps> = ({
    onEditDeduction,
    fromDate = '',
    toDate = '',
    customerId = '',
    selectedYear,
    selectedMonth,
    searchQuery,
}) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const dispatch = useAppDispatch();

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [editLoadingId, setEditLoadingId] = useState<string | null>(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Redux state
    const {
        deductionData,
        deductionLoading,
        deductionNumberOfPages,
    } = useSelector((state: any) => state.timesheet);

    // Customers list for name lookup
    const customers: any[] = useSelector((state: any) => state.customer?.customers || []);

    // Helper: translate workingDescription values
    const translateWorkingDescription = (description: string): string => {
        if (!description) return description;
        
        const descriptionMap: Record<string, string> = {
            'Travel Expenses as per attached PDF': t('TRAVEL_EXPENSES') || 'Travel Expenses',
            'Allowance outside Sweden': t('ALLOWANCE_OUTSIDE_SWEDEN') || 'Allowance Outside Sweden',
            'Allowance in Sweden': t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden',
            'Expenses for materials and tools as per attached PDF': t('MATERIALS_AND_TOOLS') || 'Materials & Tools',
            'Mileage Report': t('MILEAGE_REPORT') || 'Mileage Report',
        };
        
        return descriptionMap[description] || description;
    };

    // Helper: article number → display label
    const getDeductionLabel = (articleNumber: string, fallback: string): string => {
        const type = ARTICLE_NUMBER_TO_DEDUCTION_TYPE[articleNumber];
        if (!type) return translateWorkingDescription(fallback) || articleNumber || '-';
        switch (type) {
            case 'mileage_report':                   return t('MILEAGE_REPORT') || 'Mileage Report';
            case 'travel_expenses_pdf':              return t('TRAVEL_EXPENSES') || 'Travel Expenses';
            case 'expenses_materials_tools_pdf':     return t('MATERIALS_AND_TOOLS') || 'Materials & Tools';
            case 'allowance_outside_sweden':         return t('ALLOWANCE_OUTSIDE_SWEDEN') || 'Allowance Outside Sweden';
            case 'allowance_in_sweden':              return t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden';
            default:                                 return translateWorkingDescription(fallback) || articleNumber || '-';
        }
    };

    // Helper: customerId → customer name
    const getCustomerName = (id: number): string => {
        const found = customers.find(
            (c: any) => (c.customerId || c.id) === id
        );
        return found?.name || found?.customerName || '-';
    };

    // Load data on mount
    useEffect(() => {
        setCurrentPage(1);
        dispatch(fetchTimesheetDeductions({
            pageNumber: 1,
            tab: 2,
            fromDate: fromDate || '',
            toDate: toDate || '',
            customerId: (customerId && customerId !== 'all') ? customerId : '',
            searchQuery: searchQuery,
        }));
    }, [customerId]);

    const deductions: NotInvoicedDeduction[] = (deductionData || []).map((item: any) => ({
        id: String(item.timeSheetWorkingHourId || item.id),
        deduction: getDeductionLabel(String(item.articleNumber ?? ''), item.workingDescription),
        customerName: getCustomerName(item.customerId),
        period: item.workingDate
            ? new Date(item.workingDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
            : '-',
        amount: item.amount != null ? `${item.amount} SEK` : '-',
    }));

    const totalPages = deductionNumberOfPages || Math.ceil(deductions.length / itemsPerPage);

    const handleEdit = async (id: string) => {
        if (!onEditDeduction) return;
        try {
            setEditLoadingId(id);
            const result = await getTimesheetDeductionsApi({
                pageNumber: 1,
                tab: 2,
                fromDate: fromDate || '',
                toDate: toDate || '',
                customerId: (customerId && customerId !== 'all') ? customerId : '',
                searchQuery: id,
            });
            const list: any[] = (result?.response as any)?.timeSheetWorkingHoursV2List
                ?? result?.response
                ?? [];
            const rawItem = list.length > 0 ? list[0] : null;
            if (rawItem) {
                // Map API field names → field names the AddDeductionModal expects
                const mappedItem = {
                    ...rawItem,
                    // boolean → 'yes'/'no' string (dropdown expects string values)
                    isTaxFree:               rawItem.isTaxFreeArticle === true ? 'yes' : rawItem.isTaxFreeArticle === false ? 'no' : '',
                    showOnInvoice:           rawItem.isArticleShownInInvoice === true ? 'yes' : rawItem.isArticleShownInInvoice === false ? 'no' : '',
                    isPdfAttachedToInvoice:  rawItem.isPdfAttachedToInvoice === true ? 'yes' : rawItem.isPdfAttachedToInvoice === false ? 'no' : '',
                    // amount
                    amountExVat:             rawItem.amount,
                    // number → string (VAT dropdown options are '0','6','12','25')
                    vat:                     rawItem.vat != null ? String(rawItem.vat) : '',
                    // mileage / travel fields
                    carRegistration:         rawItem.carRegistrationNo,
                    tripStartPlace:          rawItem.tripStartedPlace,
                    tripEndPlace:            rawItem.tripEndedPlace,
                    reason:                  rawItem.reasonForTrip,
                    destination:             rawItem.allowanceDestination,
                };
                onEditDeduction(mappedItem);
            }
        } catch (err) {
            console.error('Failed to fetch deduction for edit:', err);
        } finally {
            setEditLoadingId(null);
        }
    };

    const handleDelete = (id: string) => {
        setDeleteConfirmId(id);
    };

    const handleConfirmDelete = async () => {
        if (!deleteConfirmId) return;
        try {
            setDeleteLoading(true);
            const result = await dispatch(
                deleteTimesheetWorkingHours({ timeSheetWorkingHourIds: [Number(deleteConfirmId)] })
            ).unwrap();
            if (result?.statusCode === 200 || result?.statusCode === 201) {
                setDeleteConfirmId(null);

                // Convert year and month to DateTime format for refetch
                let yearDateTime = null;
                let monthDateTime = null;
                
                if (selectedYear && selectedYear !== 'all') {
                    const yearNum = parseInt(selectedYear);
                    const monthNum = selectedMonth && selectedMonth !== 'all' ? parseInt(selectedMonth) : 1;
                    yearDateTime = `${yearNum}-${String(monthNum).padStart(2, '0')}-01T00:00:00`;
                }

                if (selectedMonth && selectedMonth !== 'all' && selectedYear && selectedYear !== 'all') {
                    const yearNum = parseInt(selectedYear);
                    const monthNum = parseInt(selectedMonth);
                    monthDateTime = `${yearNum}-${String(monthNum).padStart(2, '0')}-01T00:00:00`;
                }

                const finalCustomerId = customerId && customerId !== 'all' ? customerId : '';
                const finalSearchQuery = searchQuery || '';

                dispatch(
                    fetchTimesheetDeductions({
                        fromDate: fromDate || '',
                        toDate: toDate || '',
                        customerId: finalCustomerId,
                        pageNumber: currentPage,
                        tab: TAB_NOT_INVOICED,
                        searchQuery: finalSearchQuery,
                        year: yearDateTime,
                        month: monthDateTime,
                    })
                );
            }
        } catch (err) {
            console.error('Failed to delete deduction:', err);
        } finally {
            setDeleteLoading(false);
        }
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const calculatePaginationPages = (current: number, total: number) => {
        const pages: (number | string)[] = [];
        
        if (total <= 7) {
            for (let i = 1; i <= total; i++) {
                pages.push(i);
            }
        } else {
            if (current <= 3) {
                pages.push(1, 2, 3, '...', total);
            } else if (current >= total - 2) {
                pages.push(1, '...', total - 2, total - 1, total);
            } else {
                pages.push(1, '...', current, '...', total);
            }
        }
        
        return pages;
    };

    return (
        <>
        <div className="invoice-table-container">
            <div className="invoice-table-content">
                {deductionLoading && (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                        {t('LOADING') || 'Loading...'}
                    </div>
                )}

                {!deductionLoading && deductions.length === 0 && (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                        {t('NO_DATA_FOUND')}
                    </div>
                )}

                {!deductionLoading && deductions && deductions.length > 0 && (
                    <div className="invoice-table">
                        {!isMobile && (
                            <TableHeader>
                                <TableColumn>{t('DEDUCTIONS')}</TableColumn>
                                <TableColumn>{t('CUSTOMER_NAME_LABEL')}</TableColumn>
                                <TableColumn>{t('PERIOD')}</TableColumn>
                                <TableColumn>{t('TOTAL_AMOUNT')}</TableColumn>
                                <TableColumn>{t('ACTION')}</TableColumn>
                            </TableHeader>
                        )}

                        <TableBody>
                            {deductions.length === 0 && (
                                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                                    {t('NO_DATA_FOUND')}
                                </div>
                            )}
                            
                            {isMobile ? (
                                deductions.map((deduction) => (
                                    <MobileCardWrapper key={deduction.id}>
                                        <MobileCardRow label={t('DEDUCTION')}>
                                            <span className="deduction-name">{deduction.deduction}</span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('CUSTOMER_NAME_LABEL')}>
                                            {deduction.customerName}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('PERIOD')}>
                                            {deduction.period}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('TOTAL_AMOUNT')}>
                                            {deduction.amount}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('ACTION')}>
                                            <div className="action-buttons-mobile">
                                                <button 
                                                    className="action-button action-button--edit"
                                                    onClick={() => handleEdit(deduction.id)}
                                                    title={t('EDIT')}
                                                    disabled={editLoadingId === deduction.id}
                                                    style={{ opacity: editLoadingId === deduction.id ? 0.5 : 1 }}
                                                >
                                                    {editLoadingId === deduction.id ? '...' : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                            <path d="M1.33763 10.9955L2.28631 11.3117H2.28631L1.33763 10.9955ZM1.95469 9.14428L1.006 8.82805L1.006 8.82805L1.95469 9.14428ZM2.59139 8.11407L3.2985 8.82118L3.2985 8.82118L2.59139 8.11407ZM8.74052 1.96495L8.03341 1.25784L8.03341 1.25784L8.74052 1.96495ZM12.0351 5.25949L12.7422 5.96659H12.7422L12.0351 5.25949ZM5.88593 11.4086L5.17882 10.7015L5.17882 10.7015L5.88593 11.4086ZM4.85572 12.0453L5.17195 12.994H5.17195L4.85572 12.0453ZM3.00455 12.6624L3.32078 13.6111H3.32078L3.00455 12.6624ZM5.5482 11.7238L4.94596 10.9255L4.94596 10.9255L5.5482 11.7238ZM5.2887 11.8842L4.844 10.9885L5.2887 11.8842ZM12.8287 2.8808L11.9327 3.32482V3.32482L12.8287 2.8808ZM12.8287 4.34363L11.9327 3.89961V3.89961L12.8287 4.34363ZM9.65637 1.17128L10.1004 2.0673L10.1004 2.0673L9.65637 1.17128ZM11.1192 1.17128L10.6752 2.0673L10.6752 2.0673L11.1192 1.17128ZM2.11578 8.7113L3.01146 9.156L3.01146 9.156L2.11578 8.7113ZM2.27617 8.4518L1.47785 7.84955L1.47785 7.84956L2.27617 8.4518ZM1.03067 12.4702L0.0887613 12.8061L1.03067 12.4702ZM1.52981 12.9693L1.19393 13.9112L1.19393 13.9112L1.52981 12.9693ZM8.62398 2.0815C8.23346 1.69098 7.60029 1.69098 7.20977 2.0815C6.81924 2.47203 6.81924 3.10519 7.20977 3.49572L7.91688 2.78861L8.62398 2.0815ZM10.5043 6.79025C10.8948 7.18078 11.528 7.18078 11.9185 6.79025C12.309 6.39973 12.309 5.76656 11.9185 5.37604L11.2114 6.08315L10.5043 6.79025ZM1.33763 10.9955L2.28631 11.3117L2.90337 9.46051L1.95469 9.14428L1.006 8.82805L0.388947 10.6792L1.33763 10.9955ZM2.59139 8.11407L3.2985 8.82118L9.44762 2.67205L8.74052 1.96495L8.03341 1.25784L1.88429 7.40696L2.59139 8.11407ZM12.0351 5.25949L11.3279 4.55238L5.17882 10.7015L5.88593 11.4086L6.59304 12.1157L12.7422 5.96659L12.0351 5.25949ZM4.85572 12.0453L4.53949 11.0966L2.68832 11.7137L3.00455 12.6624L3.32078 13.6111L5.17195 12.994L4.85572 12.0453ZM5.88593 11.4086L5.17882 10.7015C4.99587 10.8845 4.96924 10.908 4.94596 10.9255L5.5482 11.7238L6.15044 12.5221C6.31038 12.4015 6.44783 12.2609 6.59304 12.1157L5.88593 11.4086ZM4.85572 12.0453L5.17195 12.994C5.36676 12.9291 5.55397 12.869 5.73341 12.7799L5.2887 11.8842L4.844 10.9885C4.81788 11.0015 4.78495 11.0148 4.53949 11.0966L4.85572 12.0453ZM5.5482 11.7238L4.94596 10.9255C4.914 10.9496 4.87986 10.9707 4.844 10.9885L5.2887 11.8842L5.73341 12.7799C5.88009 12.7071 6.0197 12.6208 6.15045 12.5221L5.5482 11.7238ZM12.0351 1.96495L11.3279 2.67205C11.8341 3.17823 11.9039 3.26671 11.9327 3.32482L12.8287 2.8808L13.7247 2.43679C13.5135 2.01045 13.1342 1.64984 12.7422 1.25784L12.0351 1.96495ZM12.0351 5.25949L12.7422 5.96659C13.1342 5.5746 13.5135 5.21398 13.7247 4.78764L12.8287 4.34363L11.9327 3.89961C11.9039 3.95773 11.8341 4.0462 11.3279 4.55238L12.0351 5.25949ZM12.8287 2.8808L11.9327 3.32482C12.0224 3.50591 12.0224 3.71852 11.9327 3.89961L12.8287 4.34363L13.7247 4.78765C14.0918 4.047 14.0918 3.17743 13.7247 2.43679L12.8287 2.8808ZM8.74052 1.96495L9.44762 2.67205C9.9538 2.16588 10.0423 2.0961 10.1004 2.0673L9.65637 1.17128L9.21236 0.275265C8.78602 0.486534 8.4254 0.86585 8.03341 1.25784L8.74052 1.96495ZM12.0351 1.96495L12.7422 1.25784C12.3502 0.865846 11.9896 0.486535 11.5632 0.275265L11.1192 1.17128L10.6752 2.0673C10.7333 2.0961 10.8218 2.16588 11.3279 2.67205L12.0351 1.96495ZM9.65637 1.17128L10.1004 2.0673C10.2815 1.97757 10.4941 1.97757 10.6752 2.0673L11.1192 1.17128L11.5632 0.275266C10.8226 -0.0917553 9.953 -0.0917553 9.21235 0.275266L9.65637 1.17128ZM1.95469 9.14428L2.90337 9.46051C2.98519 9.21505 2.9985 9.18212 3.01146 9.156L2.11578 8.7113L1.22011 8.26659C1.13102 8.44603 1.07094 8.63324 1.006 8.82805L1.95469 9.14428ZM2.59139 8.11407L1.88429 7.40696C1.73908 7.55217 1.5985 7.68962 1.47785 7.84955L2.27617 8.4518L3.07448 9.05404C3.09204 9.03076 3.11555 9.00413 3.2985 8.82118L2.59139 8.11407ZM2.11578 8.7113L3.01146 9.156C3.02927 9.12014 3.05037 9.086 3.07448 9.05404L2.27617 8.4518L1.47785 7.84956C1.37922 7.9803 1.29294 8.11991 1.22011 8.26659L2.11578 8.7113ZM1.33763 10.9955L0.388947 10.6792C0.261123 11.0627 0.14235 11.4159 0.0742851 11.7055C0.00970632 11.9803 -0.0603899 12.3878 0.0887613 12.8061L1.03067 12.4702L1.97257 12.1343C2.0263 12.285 1.97556 12.3575 2.02125 12.163C2.06346 11.9834 2.14588 11.733 2.28631 11.3117L1.33763 10.9955ZM3.00455 12.6624L2.68832 11.7137C2.26703 11.8541 2.0166 11.9365 1.83698 11.9787C1.64253 12.0244 1.71501 11.9737 1.86569 12.0274L1.52981 12.9693L1.19393 13.9112C1.61219 14.0604 2.01967 13.9903 2.29449 13.9257C2.58415 13.8576 2.9373 13.7389 3.32078 13.6111L3.00455 12.6624ZM1.03067 12.4702L0.0887613 12.8061C0.272617 13.3217 0.678346 13.7274 1.19393 13.9112L1.52981 12.9693L1.86569 12.0274C1.91555 12.0452 1.95479 12.0844 1.97257 12.1343L1.03067 12.4702ZM7.91688 2.78861L7.20977 3.49572L10.5043 6.79025L11.2114 6.08315L11.9185 5.37604L8.62398 2.0815L7.91688 2.78861Z" fill="#F2994A"/>
                                                        </svg>
                                                    )}
                                                </button>
                                                <button 
                                                    className="action-button action-button--delete"
                                                    onClick={() => handleDelete(deduction.id)}
                                                    title={t('DELETE')}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="16" viewBox="0 0 13 16" fill="none">
                                                        <path d="M1 2.66667C0.447715 2.66667 0 3.11438 0 3.66667C0 4.21895 0.447715 4.66667 1 4.66667V3.66667V2.66667ZM11.6667 4.66667C12.219 4.66667 12.6667 4.21895 12.6667 3.66667C12.6667 3.11438 12.219 2.66667 11.6667 2.66667V3.66667V4.66667ZM2.66667 3.66667C2.66667 4.21895 3.11438 4.66667 3.66667 4.66667C4.21895 4.66667 4.66667 4.21895 4.66667 3.66667H3.66667H2.66667ZM8 3.66667C8 4.21895 8.44772 4.66667 9 4.66667C9.55229 4.66667 10 4.21895 10 3.66667H9H8ZM11 3.66667L10 3.65774L9.9402 10.3601L10.9402 10.369L11.9401 10.378L12 3.67559L11 3.66667ZM6.94032 14.3333V13.3333H5.66667V14.3333V15.3333H6.94032V14.3333ZM1.66667 3.66667H0.666667V10.3333H1.66667H2.66667V3.66667H1.66667ZM1 3.66667V4.66667H1.66667V3.66667V2.66667H1V3.66667ZM1.66667 3.66667V4.66667H11V3.66667V2.66667H1.66667V3.66667ZM11 3.66667V4.66667H11.6667V3.66667V2.66667H11V3.66667ZM3.66667 3.37037H4.66667C4.66667 2.72101 5.29888 2 6.33333 2V1V0C4.42227 0 2.66667 1.40149 2.66667 3.37037H3.66667ZM6.33333 1V2C7.36779 2 8 2.72101 8 3.37037H9H10C10 1.40149 8.2444 0 6.33333 0V1ZM3.66667 3.37037H2.66667V3.66667H3.66667H4.66667V3.37037H3.66667ZM9 3.37037H8V3.66667H9H10V3.37037H9ZM5.66667 14.3333V13.3333C4.00981 13.3333 2.66667 11.9902 2.66667 10.3333H1.66667H0.666667C0.666667 13.0948 2.90524 15.3333 5.66667 15.3333V14.3333ZM10.9402 10.369L9.9402 10.3601C9.9255 12.0065 8.58672 13.3333 6.94032 13.3333V14.3333V15.3333C9.68433 15.3333 11.9156 13.1219 11.9401 10.378L10.9402 10.369Z" fill="#E33B32"/>
                                                    </svg>
                                                </button>
                                            </div>
                                        </MobileCardRow>
                                    </MobileCardWrapper>
                                ))
                            ) : (
                                deductions.map((deduction) => (
                                    <TableRow key={deduction.id}>
                                        <TableCell>
                                            <span className="deduction-name">{deduction.deduction}</span>
                                        </TableCell>
                                        <TableCell>{deduction.customerName}</TableCell>
                                        <TableCell>{deduction.period}</TableCell>
                                        <TableCell>{deduction.amount}</TableCell>
                                        <TableCell>
                                            <div className="action-buttons">
                                                <button 
                                                    className="action-button action-button--edit"
                                                    onClick={() => handleEdit(deduction.id)}
                                                    title={t('EDIT')}
                                                    disabled={editLoadingId === deduction.id}
                                                    style={{ opacity: editLoadingId === deduction.id ? 0.5 : 1 }}
                                                >
                                                    {editLoadingId === deduction.id ? '...' : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                            <path d="M1.33763 10.9955L2.28631 11.3117H2.28631L1.33763 10.9955ZM1.95469 9.14428L1.006 8.82805L1.006 8.82805L1.95469 9.14428ZM2.59139 8.11407L3.2985 8.82118L3.2985 8.82118L2.59139 8.11407ZM8.74052 1.96495L8.03341 1.25784L8.03341 1.25784L8.74052 1.96495ZM12.0351 5.25949L12.7422 5.96659H12.7422L12.0351 5.25949ZM5.88593 11.4086L5.17882 10.7015L5.17882 10.7015L5.88593 11.4086ZM4.85572 12.0453L5.17195 12.994H5.17195L4.85572 12.0453ZM3.00455 12.6624L3.32078 13.6111H3.32078L3.00455 12.6624ZM5.5482 11.7238L4.94596 10.9255L4.94596 10.9255L5.5482 11.7238ZM5.2887 11.8842L4.844 10.9885L5.2887 11.8842ZM12.8287 2.8808L11.9327 3.32482V3.32482L12.8287 2.8808ZM12.8287 4.34363L11.9327 3.89961V3.89961L12.8287 4.34363ZM9.65637 1.17128L10.1004 2.0673L10.1004 2.0673L9.65637 1.17128ZM11.1192 1.17128L10.6752 2.0673L10.6752 2.0673L11.1192 1.17128ZM2.11578 8.7113L3.01146 9.156L3.01146 9.156L2.11578 8.7113ZM2.27617 8.4518L1.47785 7.84955L1.47785 7.84956L2.27617 8.4518ZM1.03067 12.4702L0.0887613 12.8061L1.03067 12.4702ZM1.52981 12.9693L1.19393 13.9112L1.19393 13.9112L1.52981 12.9693ZM8.62398 2.0815C8.23346 1.69098 7.60029 1.69098 7.20977 2.0815C6.81924 2.47203 6.81924 3.10519 7.20977 3.49572L7.91688 2.78861L8.62398 2.0815ZM10.5043 6.79025C10.8948 7.18078 11.528 7.18078 11.9185 6.79025C12.309 6.39973 12.309 5.76656 11.9185 5.37604L11.2114 6.08315L10.5043 6.79025ZM1.33763 10.9955L2.28631 11.3117L2.90337 9.46051L1.95469 9.14428L1.006 8.82805L0.388947 10.6792L1.33763 10.9955ZM2.59139 8.11407L3.2985 8.82118L9.44762 2.67205L8.74052 1.96495L8.03341 1.25784L1.88429 7.40696L2.59139 8.11407ZM12.0351 5.25949L11.3279 4.55238L5.17882 10.7015L5.88593 11.4086L6.59304 12.1157L12.7422 5.96659L12.0351 5.25949ZM4.85572 12.0453L4.53949 11.0966L2.68832 11.7137L3.00455 12.6624L3.32078 13.6111L5.17195 12.994L4.85572 12.0453ZM5.88593 11.4086L5.17882 10.7015C4.99587 10.8845 4.96924 10.908 4.94596 10.9255L5.5482 11.7238L6.15044 12.5221C6.31038 12.4015 6.44783 12.2609 6.59304 12.1157L5.88593 11.4086ZM4.85572 12.0453L5.17195 12.994C5.36676 12.9291 5.55397 12.869 5.73341 12.7799L5.2887 11.8842L4.844 10.9885C4.81788 11.0015 4.78495 11.0148 4.53949 11.0966L4.85572 12.0453ZM5.5482 11.7238L4.94596 10.9255C4.914 10.9496 4.87986 10.9707 4.844 10.9885L5.2887 11.8842L5.73341 12.7799C5.88009 12.7071 6.0197 12.6208 6.15045 12.5221L5.5482 11.7238ZM12.0351 1.96495L11.3279 2.67205C11.8341 3.17823 11.9039 3.26671 11.9327 3.32482L12.8287 2.8808L13.7247 2.43679C13.5135 2.01045 13.1342 1.64984 12.7422 1.25784L12.0351 1.96495ZM12.0351 5.25949L12.7422 5.96659C13.1342 5.5746 13.5135 5.21398 13.7247 4.78764L12.8287 4.34363L11.9327 3.89961C11.9039 3.95773 11.8341 4.0462 11.3279 4.55238L12.0351 5.25949ZM12.8287 2.8808L11.9327 3.32482C12.0224 3.50591 12.0224 3.71852 11.9327 3.89961L12.8287 4.34363L13.7247 4.78765C14.0918 4.047 14.0918 3.17743 13.7247 2.43679L12.8287 2.8808ZM8.74052 1.96495L9.44762 2.67205C9.9538 2.16588 10.0423 2.0961 10.1004 2.0673L9.65637 1.17128L9.21236 0.275265C8.78602 0.486534 8.4254 0.86585 8.03341 1.25784L8.74052 1.96495ZM12.0351 1.96495L12.7422 1.25784C12.3502 0.865846 11.9896 0.486535 11.5632 0.275265L11.1192 1.17128L10.6752 2.0673C10.7333 2.0961 10.8218 2.16588 11.3279 2.67205L12.0351 1.96495ZM9.65637 1.17128L10.1004 2.0673C10.2815 1.97757 10.4941 1.97757 10.6752 2.0673L11.1192 1.17128L11.5632 0.275266C10.8226 -0.0917553 9.953 -0.0917553 9.21235 0.275266L9.65637 1.17128ZM1.95469 9.14428L2.90337 9.46051C2.98519 9.21505 2.9985 9.18212 3.01146 9.156L2.11578 8.7113L1.22011 8.26659C1.13102 8.44603 1.07094 8.63324 1.006 8.82805L1.95469 9.14428ZM2.59139 8.11407L1.88429 7.40696C1.73908 7.55217 1.5985 7.68962 1.47785 7.84955L2.27617 8.4518L3.07448 9.05404C3.09204 9.03076 3.11555 9.00413 3.2985 8.82118L2.59139 8.11407ZM2.11578 8.7113L3.01146 9.156C3.02927 9.12014 3.05037 9.086 3.07448 9.05404L2.27617 8.4518L1.47785 7.84956C1.37922 7.9803 1.29294 8.11991 1.22011 8.26659L2.11578 8.7113ZM1.33763 10.9955L0.388947 10.6792C0.261123 11.0627 0.14235 11.4159 0.0742851 11.7055C0.00970632 11.9803 -0.0603899 12.3878 0.0887613 12.8061L1.03067 12.4702L1.97257 12.1343C2.0263 12.285 1.97556 12.3575 2.02125 12.163C2.06346 11.9834 2.14588 11.733 2.28631 11.3117L1.33763 10.9955ZM3.00455 12.6624L2.68832 11.7137C2.26703 11.8541 2.0166 11.9365 1.83698 11.9787C1.64253 12.0244 1.71501 11.9737 1.86569 12.0274L1.52981 12.9693L1.19393 13.9112C1.61219 14.0604 2.01967 13.9903 2.29449 13.9257C2.58415 13.8576 2.9373 13.7389 3.32078 13.6111L3.00455 12.6624ZM1.03067 12.4702L0.0887613 12.8061C0.272617 13.3217 0.678346 13.7274 1.19393 13.9112L1.52981 12.9693L1.86569 12.0274C1.91555 12.0452 1.95479 12.0844 1.97257 12.1343L1.03067 12.4702ZM7.91688 2.78861L7.20977 3.49572L10.5043 6.79025L11.2114 6.08315L11.9185 5.37604L8.62398 2.0815L7.91688 2.78861Z" fill="#F2994A"/>
                                                        </svg>
                                                    )}
                                                </button>
                                                <button 
                                                    className="action-button action-button--delete"
                                                    onClick={() => handleDelete(deduction.id)}
                                                    title={t('DELETE')}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="16" viewBox="0 0 13 16" fill="none">
                                                        <path d="M1 2.66667C0.447715 2.66667 0 3.11438 0 3.66667C0 4.21895 0.447715 4.66667 1 4.66667V3.66667V2.66667ZM11.6667 4.66667C12.219 4.66667 12.6667 4.21895 12.6667 3.66667C12.6667 3.11438 12.219 2.66667 11.6667 2.66667V3.66667V4.66667ZM2.66667 3.66667C2.66667 4.21895 3.11438 4.66667 3.66667 4.66667C4.21895 4.66667 4.66667 4.21895 4.66667 3.66667H3.66667H2.66667ZM8 3.66667C8 4.21895 8.44772 4.66667 9 4.66667C9.55229 4.66667 10 4.21895 10 3.66667H9H8ZM11 3.66667L10 3.65774L9.9402 10.3601L10.9402 10.369L11.9401 10.378L12 3.67559L11 3.66667ZM6.94032 14.3333V13.3333H5.66667V14.3333V15.3333H6.94032V14.3333ZM1.66667 3.66667H0.666667V10.3333H1.66667H2.66667V3.66667H1.66667ZM1 3.66667V4.66667H1.66667V3.66667V2.66667H1V3.66667ZM1.66667 3.66667V4.66667H11V3.66667V2.66667H1.66667V3.66667ZM11 3.66667V4.66667H11.6667V3.66667V2.66667H11V3.66667ZM3.66667 3.37037H4.66667C4.66667 2.72101 5.29888 2 6.33333 2V1V0C4.42227 0 2.66667 1.40149 2.66667 3.37037H3.66667ZM6.33333 1V2C7.36779 2 8 2.72101 8 3.37037H9H10C10 1.40149 8.2444 0 6.33333 0V1ZM3.66667 3.37037H2.66667V3.66667H3.66667H4.66667V3.37037H3.66667ZM9 3.37037H8V3.66667H9H10V3.37037H9ZM5.66667 14.3333V13.3333C4.00981 13.3333 2.66667 11.9902 2.66667 10.3333H1.66667H0.666667C0.666667 13.0948 2.90524 15.3333 5.66667 15.3333V14.3333ZM10.9402 10.369L9.9402 10.3601C9.9255 12.0065 8.58672 13.3333 6.94032 13.3333V14.3333V15.3333C9.68433 15.3333 11.9156 13.1219 11.9401 10.378L10.9402 10.369Z" fill="#E33B32"/>
                                                    </svg>
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 0 && (
                    <div className="invoice-table-pagination">
                        <div className="pagination-controls">
                            <button
                                className="pagination-button pagination-button--prev"
                                onClick={goToPreviousPage}
                                disabled={currentPage === 1}
                            >
                                <ArrowLeftIcon />
                                <span>{t('PREV')}</span>
                            </button>

                            {calculatePaginationPages(currentPage, totalPages).map((page, index) => {
                                if (page === '...') {
                                    return (
                                        <button
                                            key={`ellipsis-${index}`}
                                            className="pagination-button pagination-button--number"
                                            disabled
                                        >
                                            ...
                                        </button>
                                    );
                                }
                                return (
                                    <button
                                        key={page}
                                        className={`pagination-button pagination-button--number${page === currentPage ? ' pagination-button--active' : ''}`}
                                        onClick={() => setCurrentPage(page as number)}
                                    >
                                        {page}
                                    </button>
                                );
                            })}

                            <button
                                className="pagination-button pagination-button--next"
                                onClick={goToNextPage}
                                disabled={currentPage === totalPages}
                            >
                                <span>{t('NEXT')}</span>
                                <ArrowRightIcon />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
        <DeleteConfirmModal
            open={deleteConfirmId !== null}
            loading={deleteLoading}
            onCancel={() => setDeleteConfirmId(null)}
            onConfirm={handleConfirmDelete}
            t={t}
        />
        </>
    );
};

// Delete confirmation modal — rendered outside main component tree via a simple inline modal
function DeleteConfirmModal({
    open,
    loading,
    onCancel,
    onConfirm,
    t,
}: {
    open: boolean;
    loading: boolean;
    onCancel: () => void;
    onConfirm: () => void;
    t: (key: string) => string;
}) {
    if (!open) return null;
    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                backgroundColor: 'rgba(0,0,0,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onClick={onCancel}
        >
            <div
                style={{
                    background: '#fff', borderRadius: '12px', padding: '24px',
                    minWidth: '320px', maxWidth: '420px', width: '90%',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#252525', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                        {t('CONFIRM_DELETE') || 'Confirm Delete'}
                    </h3>
                    <button
                        onClick={onCancel}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#68727d' }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>
                {/* Body */}
                <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#444', fontFamily: 'Plus Jakarta Sans, sans-serif', lineHeight: '1.5' }}>
                    {t('ARE_YOU_SURE_DELETE_ENTRY') || 'Are you sure you want to delete this entry?'}
                </p>
                {/* Footer */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                        onClick={onCancel}
                        disabled={loading}
                        style={{
                            padding: '8px 20px', borderRadius: '8px', border: '1px solid #eaebf0',
                            background: '#fff', color: '#252525', fontSize: '14px', fontWeight: 500,
                            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif',
                        }}
                    >
                        {t('CANCEL') || 'Cancel'}
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        style={{
                            padding: '8px 20px', borderRadius: '8px', border: 'none',
                            background: '#dc3545', color: '#fff', fontSize: '14px', fontWeight: 500,
                            cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
                            fontFamily: 'Plus Jakarta Sans, sans-serif',
                        }}
                    >
                        {loading ? '...' : (t('YES') || 'Yes')}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default NotInvoicedSection;
